/**
 * pp-main-product — client-side helpers
 *
 * Responsibilities:
 *   - Thumbnail click swaps the main gallery image
 *   - Variant option selection updates the hidden variant id so the
 *     product form submits the right SKU
 *   - Quantity +/- buttons adjust the qty input and trigger a price
 *     recompute
 *   - Reactive price display: total = variantPrice * qty; when a HULK
 *     volume tier is active (communicated via `pp:tier-change` event
 *     from pp-volume-discount.js), show the discounted total with the
 *     original strike-through and a "Ahorras $X" savings line.
 *
 * Does NOT do AJAX cart updates yet — that belongs to Dawn's cart
 * drawer / cart-notification which intercept form submission.
 */

class PpMainProduct {
  constructor(root) {
    this.root = root;
    this.mainImageWrap = root.querySelector('[data-pp-main-image]');
    this.mainImage = this.mainImageWrap ? this.mainImageWrap.querySelector('img') : null;
    this.thumbs = root.querySelectorAll('[data-pp-thumb]');
    this.dots = root.querySelectorAll('[data-pp-dot]');
    this.slidesContainer = root.querySelector('[data-pp-slides]');
    this.slides = root.querySelectorAll('[data-pp-slide-index]');
    this.galleryPrevBtn = root.querySelector('[data-pp-gallery-prev]');
    this.galleryNextBtn = root.querySelector('[data-pp-gallery-next]');

    /* Image lightbox — modal de zoom para la galería. El <dialog> y el
     * manifest JSON viven en pp-image-lightbox.liquid render-eado en
     * la misma section. */
    this.lightbox = root.querySelector('[data-pp-modal="image-lightbox"]');
    this.lightboxImg = root.querySelector('[data-pp-lightbox-img]');
    this.lightboxCounter = root.querySelector('[data-pp-lightbox-counter]');
    this.lightboxPrevBtn = root.querySelector('[data-pp-lightbox-prev]');
    this.lightboxNextBtn = root.querySelector('[data-pp-lightbox-next]');
    this.lightboxManifest = null;
    const manifestEl = root.querySelector('[data-pp-lightbox-manifest]');
    if (manifestEl && manifestEl.textContent) {
      try {
        this.lightboxManifest = JSON.parse(manifestEl.textContent);
      } catch (e) {
        this.lightboxManifest = null;
      }
    }
    this.currentSlide = 0;
    this.optionInputs = root.querySelectorAll('[data-pp-option-input]');
    this.variantIdInput = root.querySelector('[data-pp-variant-id]');
    this.qtyInput = root.querySelector('[data-pp-qty-input]');
    this.qtyDecrease = root.querySelector('[data-pp-qty-decrease]');
    this.qtyIncrease = root.querySelector('[data-pp-qty-increase]');
    this.selectedValueLabels = root.querySelectorAll('[data-pp-selected-value]');

    this.priceBlock = root.querySelector('[data-pp-price-block]');
    this.priceCurrentEl = root.querySelector('[data-pp-price-current]');
    this.priceCaptionEl = this.priceBlock ? this.priceBlock.querySelector('.pp-main-product__price-caption') : null;
    this.priceOriginalEl = root.querySelector('[data-pp-price-original]');
    this.priceOriginalGroupEl = root.querySelector('[data-pp-price-original-group]');
    this.priceRightEl = root.querySelector('[data-pp-price-right]');
    this.priceSavingsEl = root.querySelector('[data-pp-price-savings]');
    // Dos etiquetas: la de desktop (junto al tachado) y la de celular
    // (junto al precio grande). CSS muestra una u otra; el JS llena ambas.
    this.priceBadgeEls = root.querySelectorAll('[data-pp-price-badge]');
    this.priceRowEl = root.querySelector('[data-pp-price-row]');
    this.chipUnitValueEl = root.querySelector('[data-pp-chip-unit-value]');
    this.chipTotalValueEl = root.querySelector('[data-pp-chip-total-value]');

    // Sticky buy-bar — aparece al scrollear más allá del CTA principal
    this.stickyBar = root.querySelector('[data-pp-sticky]');
    this.stickyCurrentEl = root.querySelector('[data-pp-sticky-current]');
    this.stickyChipUnitValueEl = root.querySelector('[data-pp-sticky-chip-unit]');

    this.stickyChipTotalValueEl = root.querySelector('[data-pp-sticky-chip-total]');
    // Mobile-only sticky price block (pack · price · per-unit). Liquid
    // renders inline en sections/pp-main-product.liquid; JS lo updatea
    // con qty multiplied + volume tier discount aplicado.
    this.stickyMobilePackEl = root.querySelector('[data-pp-sticky-mobile-pack]');
    this.stickyMobileCurrentEl = root.querySelector('[data-pp-sticky-mobile-current]');
    this.stickyMobilePerUnitEl = root.querySelector('[data-pp-sticky-mobile-per-unit]');
    this.stickyQtyInput = root.querySelector('[data-pp-sticky-qty-input]');
    this.stickyQtyDecrease = root.querySelector('[data-pp-sticky-qty-decrease]');
    this.stickyQtyIncrease = root.querySelector('[data-pp-sticky-qty-increase]');
    this.stickySubmit = root.querySelector('[data-pp-sticky-submit]');
    this.mainCta = root.querySelector('.pp-main-product__cta--buy');

    /* Cart state sync. TB-1 (§30, 24-oct-2026): el botón SIEMPRE dice
     * AGREGAR y el contador es lo que vas a agregar (empieza en 1), como
     * Shopify, Mercado Libre y Amazon. Antes el botón decía «Actualizar ±N» /
     * «N en carrito». La línea «Ya tienes N en tu carrito» salió el
     * 26-oct-2026 (Dirección: ya lo dice el ícono del carrito). La cantidad
     * del carrito se cambia en el carrito. Re-sync en cada `pp:cart-updated`. */
    this.cart = null;
    this.cartQty = 0;

    if (this.priceBlock) {
      this.basePrice = parseInt(this.priceBlock.dataset.ppVariantPrice, 10) || 0;
      this.comparePrice = parseInt(this.priceBlock.dataset.ppComparePrice, 10) || 0;
      this.unitCount = parseInt(this.priceBlock.dataset.ppUnitCount, 10) || 0;
      this.unitWord = this.priceBlock.dataset.ppUnitWord || '';
      this.unitSingular = this.priceBlock.dataset.ppUnitSingular || '';
      this.sellBy = (this.priceBlock.dataset.ppSellBy || 'paquete').toLowerCase();
      // Pactos B2B (vitrina veraz, 4-sep): ids pactados + escalera
      // contextual {vid: [[min, cents], ...]} emitidos por el Liquid
      // desde el letrero de la CompanyLocation — la MISMA señal que usa
      // la Function al cobrar. Fail-open: attr ausente/ilegible = sin
      // pactos, conducta genérica de siempre.
      this.pactadoIds = [];
      this.pactoBreaks = {};
      try {
        this.pactadoIds = JSON.parse(this.priceBlock.dataset.ppPactados || '[]').map(String);
        this.pactoBreaks = JSON.parse(this.priceBlock.dataset.ppBreaks || '{}');
      } catch (err) {
        this.pactadoIds = [];
        this.pactoBreaks = {};
      }
    } else {
      this.basePrice = 0;
      this.comparePrice = 0;
      this.unitCount = 0;
      this.unitWord = '';
      this.unitSingular = '';
      this.sellBy = 'paquete';
      this.pactadoIds = [];
      this.pactoBreaks = {};
    }
    this.activeTierOff = 0;

    this.productJson = null;
    this.fetchProductJson();

    this.bindEvents();
    this.recalcPrice();

    // Initial cart fetch + listener para sync con otras UIs (cart drawer,
    // product cards en collection, cross-sell, etc.)
    this.loadCart();
    document.addEventListener('pp:cart-updated', (e) => this.handleCartUpdated(e));
  }

  async fetchProductJson() {
    const url = window.location.pathname.split('?')[0] + '.js';
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      this.productJson = await res.json();
    } catch (err) {
      console.error('[pp-main-product] Failed to fetch product json:', err);
    }
  }

  bindEvents() {
    // Intercept add-to-cart: AJAX add + open the PP cart drawer instead
    // of letting Shopify navigate to /cart. Listened to by pp-cart-drawer.js.
    const form = this.root.querySelector('.pp-main-product__form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleAddToCart(e, form));
    }

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.handleThumb(thumb));
    });

    // Dots son indicadores PASIVOS (no clickeables) — el user navega
    // entre imágenes con swipe del scroll-snap del track. Los dots
    // solo reflejan qué imagen está visible vía la clase is-active
    // (se actualiza desde el IntersectionObserver del track, no
    // requiere click handler aquí). Antes había addEventListener
    // pero el visual de touch target 44x44 quedaba feo y el user
    // pidió regresar al diseño original chiquito 8x8.

    // Desktop-only flechas prev/next sobre la imagen. goToSlide wrappea
    // automáticamente (last → first y viceversa), así que no need de bounds.
    if (this.galleryPrevBtn) {
      this.galleryPrevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.goToSlide(this.currentSlide - 1, { animate: true });
      });
    }
    if (this.galleryNextBtn) {
      this.galleryNextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.goToSlide(this.currentSlide + 1, { animate: true });
      });
    }

    // Lightbox triggers + navegación. La imagen principal del carousel
    // abre el lightbox en el slide actual. El video btn y los arrows
    // del carousel hacen stopPropagation para no abrir el lightbox.
    this.bindLightbox();

    // Mobile swipe en la imagen principal → next/prev con loop. Solo
    // se activa si hay >1 imagen y el viewport es mobile.
    this.bindGallerySwipe();

    // Mobile-only bottom sheets (variant / priceInfo). Triggers viven en el
    // price block (priceInfo) y la variant summary card (variant). La de
    // volumen se retiró en la Fase 1A: los renglones van abiertos en la
    // ficha. Cierra al tap en backdrop o close button. Body scroll lock
    // mientras está abierto.
    this.bindSheetEvents();

    // Mobile-only product tabs pills (Ficha / Desc / Uso / FAQ). Click
    // en pill → toggle is-active + show/hide del panel correspondiente.
    this.bindProductTabsEvents();

    // Reviews expand button — remueve is-collapsed de las reseñas
    // ocultas (idx >= 5 del Liquid) y se autoremueve. One-shot.
    this.bindReviewsExpand();

    // Sobre-pedido response-time message context-aware al horario MX
    // (America/Monterrey). Solo se ejecuta si el producto es sobre_pedido
    // y existe la pill data-pp-response-time.
    this.updateResponseTimeMessage();

    // Generic modal wiring: any element with [data-pp-modal-open="NAME"]
    // toggles the <dialog> with matching [data-pp-modal="NAME"]. Close
    // via [data-pp-modal-close] inside the dialog OR backdrop click.
    //
    // Lazy iframes: any <iframe data-pp-lazy-src="..."> inside a modal
    // gets its src set on open and cleared on close. Used by the video
    // modal so the YouTube player doesn't preload on page render and
    // so playback stops when the modal closes.
    this.root.querySelectorAll('[data-pp-modal-open]').forEach((btn) => {
      const name = btn.dataset.ppModalOpen;
      const modal = this.root.querySelector(`[data-pp-modal="${name}"]`);
      if (!modal) return;
      btn.addEventListener('click', () => {
        modal.querySelectorAll('iframe[data-pp-lazy-src]').forEach((iframe) => {
          if (!iframe.src || iframe.src === 'about:blank' || iframe.src === window.location.href) {
            iframe.src = iframe.dataset.ppLazySrc;
          }
        });
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          modal.setAttribute('open', '');
        }
      });
    });
    this.root.querySelectorAll('dialog[data-pp-modal]').forEach((modal) => {
      const closeBtn = modal.querySelector('[data-pp-modal-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => modal.close());
      modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.close();
      });
      modal.addEventListener('close', () => {
        modal.querySelectorAll('iframe[data-pp-lazy-src]').forEach((iframe) => {
          iframe.src = '';
        });
      });
    });

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange(input));
    });

    if (this.qtyDecrease) {
      this.qtyDecrease.addEventListener('click', () => this.adjustQty(-1));
    }
    if (this.qtyIncrease) {
      this.qtyIncrease.addEventListener('click', () => this.adjustQty(1));
    }
    if (this.qtyInput) {
      this.qtyInput.addEventListener('input', () => this.recalcPrice());
      this.qtyInput.addEventListener('change', () => {
        this.acomodarQtyTecleada();
        this.recalcPrice();
      });
    }

    this.root.addEventListener('pp:tier-change', (e) => {
      const off = e.detail && typeof e.detail.off === 'number' ? e.detail.off : 0;
      this.activeTierOff = off;
      this.recalcPrice();
    });

    this.bindEscaleraPactada();
    this.bindStickyBar();
    this.bindCopiarSku();
  }

  /**
   * UN CLIC COPIA EL SKU (26-oct-2026, Dirección): «P-1121» entero al
   * portapapeles y «Copiado» 1.6 s junto al código. Sin permiso del
   * portapapeles (http, navegador viejo) cae a seleccionar el texto y
   * `execCommand('copy')`; si tampoco, el código queda seleccionado entero
   * para Cmd/Ctrl+C.
   */
  bindCopiarSku() {
    const boton = this.root.querySelector('[data-pp-copiar-sku]');
    if (!boton) return;
    const aviso = boton.querySelector('[data-pp-sku-aviso]');
    const valor = boton.querySelector('.pp-main-product__sku-valor');
    let reloj = null;
    const avisar = () => {
      if (!aviso) return;
      aviso.textContent = boton.getAttribute('data-pp-copiado') || '';
      clearTimeout(reloj);
      reloj = setTimeout(() => { aviso.textContent = ''; }, 1600);
    };
    const seleccionar = () => {
      if (!valor || !window.getSelection) return false;
      const rango = document.createRange();
      rango.selectNodeContents(valor);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(rango);
      return true;
    };
    const aLaAntigua = () => {
      let ok = false;
      if (seleccionar()) {
        try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      }
      if (ok) avisar();
    };
    boton.addEventListener('click', () => {
      const sku = boton.getAttribute('data-pp-copiar-sku') || '';
      if (!sku) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(sku).then(avisar, aLaAntigua);
      } else {
        aLaAntigua();
      }
    });
  }

  /**
   * Escalera pactada (snippet pp-pacto-escalera): sus escalones son
   * <button data-pp-pacto-tier> y los escucha ESTA clase, no
   * pp-volume-discount.js — ese widget re-scorea el metafield generico
   * y volveria a pintar "pacto - 14%", la mentira que A3 enterro.
   *
   * El clic NO calcula precio: solo pone la cantidad y deja que
   * recalcPrice() (que ya lee data-pp-breaks via pactoUnitCentsFor)
   * pinte lo que el checkout SI cobra. Un solo dueno de la regla.
   */
  bindEscaleraPactada() {
    this.pactoTierBtns = Array.from(
      this.root.querySelectorAll('[data-pp-pacto-tier]')
    );
    if (!this.pactoTierBtns.length || !this.qtyInput) return;

    for (const btn of this.pactoTierBtns) {
      btn.addEventListener('click', () => {
        const min = parseInt(btn.getAttribute('data-pp-tier-min'), 10);
        if (!min || min < 1) return;
        this.qtyInput.value = String(min);
        this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /**
   * Deja marcado el escalon pactado que de verdad rige a esta cantidad:
   * el de minimo mas alto que qty ya alcanzo. Se llama desde
   * recalcPrice() para que el resaltado no pueda desincronizarse del
   * precio — si el numero cambia, la marca cambia con el.
   */
  sincronizarEscaleraPactada(qty) {
    if (!this.pactoTierBtns || !this.pactoTierBtns.length) return;
    let activo = null;
    let mejorMin = -1;
    for (const btn of this.pactoTierBtns) {
      const min = parseInt(btn.getAttribute('data-pp-tier-min'), 10) || 0;
      if (qty >= min && min > mejorMin) {
        mejorMin = min;
        activo = btn;
      }
    }
    for (const btn of this.pactoTierBtns) {
      const esActivo = btn === activo;
      btn.classList.toggle('is-active', esActivo);
      btn.setAttribute('aria-pressed', esActivo ? 'true' : 'false');
    }
  }

  /**
   * Sticky buy-bar wiring:
   *   - IntersectionObserver sobre el CTA principal: cuando sale del
   *     viewport (scroll down), mostrar el sticky; cuando vuelve, ocultar.
   *   - Qty bidireccional: cambios en el sticky sincronizan el input
   *     principal (que dispara recalcPrice vía su propio 'input' handler).
   *   - Submit del sticky: re-dispara el click del CTA principal para
   *     que el form product de Shopify lo capture con su cart drawer.
   */
  bindStickyBar() {
    if (!this.stickyBar || !this.mainCta) return;

    // Mobile (≤749px): sticky CTA permanente, siempre visible. Rediseño
    // 2026-05-20 Batch 3 — antes aparecía on-scroll vía IntersectionObserver,
    // ahora vive arriba del tab bar de forma fija. Forzar is-visible
    // y skip del observer en mobile.
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Estado: el sticky se muestra cuando (a) hemos pasado el CTA principal
    // y (b) el footer NO está visible. Ambas señales vienen de
    // IntersectionObserver. setStickyVisible es el toggle unificado para
    // que ambos sensores actualicen consistentemente.
    this.stickyState = { pastCta: false, footerVisible: false };
    const setStickyVisible = (visible) => {
      this.stickyBar.classList.toggle('is-visible', visible);
      // Usar `inert` (HTML nativo) en vez de aria-hidden + tabindex hacks.
      // inert atómicamente hace el subtree no focusable + no anunciable a
      // screen readers — soluciona el "aria-hidden contains focusable
      // descendants" que PageSpeed marcaba en initial state cuando los
      // buttons Cantidad / Sumar / Restar quedaban focusables dentro de
      // un wrapper con aria-hidden=true.
      if (visible) {
        this.stickyBar.removeAttribute('inert');
      } else {
        this.stickyBar.setAttribute('inert', '');
      }
      // Sync clase en body — el chat widget escucha esta clase para
      // saltarse arriba (más robusto que :has() para todos browsers).
      document.body.classList.toggle('pp-pdp-sticky-visible', visible);
      // Belt + suspenders: aplicar inline style al chat widget también
      // para no depender del CSS file que puede estar cacheado en CDN.
      // Sticky bar mide ~72px, dejamos 16px de breath = 88px de bottom.
      const chatWidget = document.getElementById('pp-chat-widget');
      if (chatWidget) {
        chatWidget.style.transition = 'bottom 240ms cubic-bezier(0.2, 0.7, 0.3, 1)';
        chatWidget.style.bottom = visible ? '88px' : '';
      }
    };
    const recompute = () => {
      const visible = this.stickyState.pastCta && !this.stickyState.footerVisible;
      setStickyVisible(visible);
    };

    if (isMobile) {
      // Mobile: sticky CTA permanente, siempre visible y SIEMPRE activa —
      // también en el pie. pp-mobile-pdp.css la deja pintada con
      // transform: none, así que apagarla en el pie la dejaba a la vista
      // pero inert (26-oct-2026). En vez de esconderse, la página reserva
      // abajo su alto (--pp-barra-compra-alto) y el pie termina arriba de ella.
      this.stickyState.pastCta = true;
      recompute();
      const reservar = () => {
        document.documentElement.style.setProperty('--pp-barra-compra-alto', `${Math.ceil(this.stickyBar.getBoundingClientRect().height)}px`);
      };
      reservar();
      if ('ResizeObserver' in window) new ResizeObserver(reservar).observe(this.stickyBar);
      // El precio se dice UNA vez (T5c, Revisión de Dirección #2, 27-oct-2026):
      // mientras el precio grande está en pantalla, la barra no repite
      // «25 cajas · $345 · $13.80/caja»; sólo cantidad, AGREGAR y personalizar.
      const precioGrande = this.priceCurrentEl;
      if (precioGrande && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.stickyBar.classList.toggle('pp-barra--precio-a-la-vista', entry.isIntersecting);
          });
        }, { threshold: 0, rootMargin: `0px 0px -${Math.ceil(this.stickyBar.getBoundingClientRect().height)}px 0px` }).observe(precioGrande);
      }
      return this.bindStickyControls();
    } else if ('IntersectionObserver' in window) {
      const ctaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const rect = entry.boundingClientRect;
            this.stickyState.pastCta = !entry.isIntersecting && rect.top < 0;
            recompute();
          });
        },
        { threshold: 0, rootMargin: '0px 0px -40px 0px' }
      );
      ctaObserver.observe(this.mainCta);
    }

    // Footer observer (aplica en mobile y desktop) — cuando el footer del
    // sitio entra al viewport, ocultar el sticky para no tapar el
    // contenido del footer y permitir scroll completo hasta abajo.
    const footer = document.querySelector('footer.footer, .pp-footer, footer[role="contentinfo"], #footer');
    if (footer && 'IntersectionObserver' in window) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.stickyState.footerVisible = entry.isIntersecting;
            recompute();
          });
        },
        { threshold: 0 }
      );
      footerObserver.observe(footer);
    }

    this.bindStickyControls();
  }

  /** Cantidad y AGREGAR de la barra (celular y escritorio). */
  bindStickyControls() {
    if (this.stickyQtyDecrease) {
      this.stickyQtyDecrease.addEventListener('click', () => this.adjustStickyQty(-1));
    }
    if (this.stickyQtyIncrease) {
      this.stickyQtyIncrease.addEventListener('click', () => this.adjustStickyQty(1));
    }
    if (this.stickyQtyInput) {
      this.stickyQtyInput.addEventListener('input', () => this.syncStickyToMain());
      this.stickyQtyInput.addEventListener('change', () => this.syncStickyToMain());
    }

    if (this.stickySubmit) {
      this.stickySubmit.addEventListener('click', () => {
        if (this.stickySubmit.disabled) return;
        // Delegamos al form principal. Así cae en el mismo flow de
        // Shopify (cart drawer / cart-notification) sin duplicar form.
        if (this.mainCta) this.mainCta.click();
      });
    }
  }

  adjustStickyQty(delta) {
    if (!this.stickyQtyInput) return;
    // El «−/+» de la barra fija es el MISMO que el de la ficha (27-oct-2026,
    // T4): respeta mínimo y múltiplo del proveedor. Antes iba de 1 en 1 y,
    // con un múltiplo > 1, el «−» redondeaba hacia arriba y no hacía nada.
    // recalcPrice copia la cantidad de vuelta a la barra.
    if (this.qtyInput) {
      this.adjustQty(delta);
      return;
    }
    const current = parseInt(this.stickyQtyInput.value, 10) || 1;
    const next = Math.max(1, current + delta);
    this.stickyQtyInput.value = String(next);
    this.syncStickyToMain();
  }

  syncStickyToMain() {
    if (!this.stickyQtyInput || !this.qtyInput) return;
    const v = parseInt(this.stickyQtyInput.value, 10);
    const next = isNaN(v) || v < 1 ? 1 : v;
    if (parseInt(this.qtyInput.value, 10) !== next) {
      this.qtyInput.value = String(next);
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Thumb / dot click → goToSlide.
   */
  handleThumb(thumb) {
    const idx = Array.from(this.thumbs).indexOf(thumb);
    if (idx < 0) return;
    this.goToSlide(idx, { animate: true });
  }

  /**
   * Mueve el carousel a la slide `index`, actualiza estado visual de
   * thumbs y dots. `animate: true` activa transition CSS para snap
   * suave; false aplica instant para wrap-around invisible.
   *
   * Fallback: si no hay slidesContainer (caso single-image),
   * solo actualiza los indicadores (no hay nada que trasladar).
   */
  goToSlide(index, { animate = true } = {}) {
    const len = this.slides.length || this.thumbs.length;
    if (len === 0) return;

    const wrapped = ((index % len) + len) % len;
    this.currentSlide = wrapped;

    if (this.slidesContainer) {
      this.slidesContainer.style.transition = animate
        ? 'transform 280ms cubic-bezier(0.32, 0.72, 0.32, 1)'
        : 'none';
      this.slidesContainer.style.transform = `translateX(${-wrapped * 100}%)`;
    }

    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === wrapped));
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === wrapped));

    // Scrollear el thumb activo a viewport (sin scrollear la página).
    // inline:nearest = solo scroll horizontal del strip cuando el thumb
    // está fuera del viewport del contenedor (overflow-x:auto). block:nearest
    // evita scroll vertical. Si el thumb ya es visible, no-op.
    const activeThumb = this.thumbs[wrapped];
    if (activeThumb && activeThumb.scrollIntoView) {
      activeThumb.scrollIntoView({
        behavior: animate ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  /**
   * Sobre-pedido pill — toggle de los 3 mensajes pre-renderizados
   * según horario laboral en zona América/Monterrey (UTC-6 sin DST).
   *
   * Los 3 mensajes existen en el DOM (Liquid render-eados estáticos);
   * este método solo decide cuál muestra (hidden attr) y swap el icono.
   * Sin innerHTML — todo es markup estático server-rendered, JS solo
   * cambia atributos.
   *
   * Reglas:
   *   - Lunes a Viernes, 9am-6pm  → variant business  (clock icon)
   *   - Lunes a Viernes, off-hours → variant offhours (email icon)
   *   - Sábado / Domingo           → variant weekend  (email icon)
   *
   * Re-evalúa cada 60s por si el user deja la pestaña abierta y cruza
   * un boundary horario.
   */
  updateResponseTimeMessage() {
    const msgs = this.root.querySelectorAll('[data-pp-response-msg]');
    const iconEl = this.root.querySelector('[data-pp-response-icon]');
    if (!msgs || msgs.length === 0) return;

    const render = () => {
      const mxNow = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'America/Monterrey' })
      );
      const day = mxNow.getDay();
      const hour = mxNow.getHours();
      const isWeekday = day >= 1 && day <= 5;
      const isBusinessHour = hour >= 9 && hour < 18;

      let activeMode = 'weekend';
      if (isWeekday && isBusinessHour) activeMode = 'business';
      else if (isWeekday) activeMode = 'offhours';

      msgs.forEach((el) => {
        el.hidden = el.dataset.ppResponseMsg !== activeMode;
      });
      if (iconEl) iconEl.textContent = activeMode === 'business' ? '⏱' : '✉';
    };

    render();
    setInterval(render, 60000);
  }

  /**
   * Image lightbox — click en la imagen principal abre el modal con la
   * versión 2048px. Navegación prev/next dentro del modal sincronizada
   * con el carousel (al cerrar, gallery queda en la última imagen vista).
   * ESC y arrow keys también bindeadas cuando el modal está abierto.
   */
  bindLightbox() {
    if (!this.lightbox || !this.lightboxImg || !this.lightboxManifest || !this.mainImageWrap) {
      return;
    }

    this.lightboxIndex = 0;

    // Click en la imagen del carousel → abre lightbox. Excluimos clicks
    // en botones (video btn, arrows, dots) usando target.closest('button').
    this.mainImageWrap.addEventListener('click', (e) => {
      if (e.target.closest('button')) return;
      this.openLightbox(this.currentSlide || 0);
    });

    // Y con teclado. El contenedor lleva role="button" tabindex="0" desde el
    // 5-sep-2026; sin esto el rol mentiría — anunciaría "botón" algo que no
    // responde a Enter. La regla de la casa es que todo lo interactivo
    // funciona con teclado solo.
    this.mainImageWrap.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      // Los botones de adentro (video, flechas, puntos) se manejan solos.
      if (e.target.closest('button')) return;
      e.preventDefault(); // Espacio, si no, hace scroll
      this.openLightbox(this.currentSlide || 0);
    });

    if (this.lightboxPrevBtn) {
      this.lightboxPrevBtn.addEventListener('click', () => {
        this.lightboxGoTo(this.lightboxIndex - 1);
      });
    }
    if (this.lightboxNextBtn) {
      this.lightboxNextBtn.addEventListener('click', () => {
        this.lightboxGoTo(this.lightboxIndex + 1);
      });
    }

    // Arrow keys cuando el lightbox está abierto.
    this.lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.lightboxGoTo(this.lightboxIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.lightboxGoTo(this.lightboxIndex + 1);
      }
    });

    // Al cerrar, sync el carousel principal al last viewed slide.
    this.lightbox.addEventListener('close', () => {
      if (this.lightboxIndex !== this.currentSlide) {
        this.goToSlide(this.lightboxIndex, { animate: false });
      }
    });

    // Click en el área alrededor de la imagen (inner del modal pero
    // fuera de la imagen y de cualquier botón) → cerrar. El handler
    // genérico de modal cierra cuando target === modal pero como el
    // inner llena el dialog 100%, ese handler nunca dispara aquí.
    const inner = this.lightbox.querySelector('.pp-image-lightbox__inner');
    if (inner) {
      inner.addEventListener('click', (e) => {
        if (e.target.closest('img, button, [data-pp-lightbox-counter]')) return;
        this.lightbox.close();
      });
    }
  }

  openLightbox(index) {
    if (!this.lightbox || !this.lightboxManifest) return;
    const len = this.lightboxManifest.length;
    if (len === 0) return;
    const wrapped = ((index % len) + len) % len;
    this.lightboxGoTo(wrapped, { skipWrap: true });
    if (typeof this.lightbox.showModal === 'function') {
      this.lightbox.showModal();
    } else {
      this.lightbox.setAttribute('open', '');
    }
  }

  lightboxGoTo(index, { skipWrap = false } = {}) {
    if (!this.lightboxImg || !this.lightboxManifest) return;
    const len = this.lightboxManifest.length;
    if (len === 0) return;
    const wrapped = skipWrap ? index : ((index % len) + len) % len;
    this.lightboxIndex = wrapped;
    const media = this.lightboxManifest[wrapped];
    if (!media) return;
    this.lightboxImg.src = media.url;
    if (media.url_2x) {
      this.lightboxImg.srcset = media.url + ' 1x, ' + media.url_2x + ' 2x';
    }
    this.lightboxImg.alt = media.alt || '';
    if (media.width) this.lightboxImg.setAttribute('width', media.width);
    if (media.height) this.lightboxImg.setAttribute('height', media.height);
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = (wrapped + 1) + ' / ' + len;
    }
  }

  /**
   * Drag horizontal real: el track sigue el dedo en touchmove, y en
   * touchend snapea a la slide más cercana (o avanza si el delta cruzó
   * el umbral de 25% del ancho de la slide).
   *
   * Lock direccional: si el primer movimiento es más vertical que
   * horizontal, soltamos el control para no robar scroll. Si es más
   * horizontal, preventDefault para evitar scroll mientras dragueamos.
   */
  bindGallerySwipe() {
    if (!this.slidesContainer || this.slides.length < 2 || !this.mainImageWrap) return;

    const SWIPE_THRESHOLD_RATIO = 0.25; // 25% del ancho de la slide
    const VELOCITY_THRESHOLD = 0.5;     // px/ms

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let lastX = 0;
    let lastTime = 0;
    let slideWidth = 0;
    let baseTranslate = 0;
    let tracking = false;
    let locked = null; // null | 'horizontal' | 'vertical'

    const setTransform = (px) => {
      this.slidesContainer.style.transition = 'none';
      this.slidesContainer.style.transform = `translateX(${px}px)`;
    };

    this.mainImageWrap.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastX = startX;
      startTime = Date.now();
      lastTime = startTime;
      slideWidth = this.mainImageWrap.offsetWidth;
      baseTranslate = -this.currentSlide * slideWidth;
      tracking = true;
      locked = null;
    }, { passive: true });

    this.mainImageWrap.addEventListener('touchmove', (e) => {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      // Lock direccional en el primer move significativo (>8px)
      if (locked === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          locked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
      }

      if (locked !== 'horizontal') return;

      // Bloquear scroll mientras dragueamos
      if (e.cancelable) e.preventDefault();

      let nextTranslate = baseTranslate + dx;

      // Resistencia ("rubber band") en los bordes para que el usuario
      // sienta que hay edge — wrap-around es loop pero damos feedback.
      const minTranslate = -(this.slides.length - 1) * slideWidth;
      const maxTranslate = 0;
      if (nextTranslate > maxTranslate) {
        nextTranslate = maxTranslate + (nextTranslate - maxTranslate) * 0.4;
      } else if (nextTranslate < minTranslate) {
        nextTranslate = minTranslate + (nextTranslate - minTranslate) * 0.4;
      }

      setTransform(nextTranslate);

      lastX = t.clientX;
      lastTime = Date.now();
    }, { passive: false });

    const endHandler = (e) => {
      if (!tracking) return;
      tracking = false;
      const lock = locked;
      locked = null;

      if (lock !== 'horizontal') return;

      const t = e.changedTouches ? e.changedTouches[0] : { clientX: lastX };
      const dx = t.clientX - startX;
      const dt = Date.now() - startTime;
      const velocity = Math.abs(dx) / Math.max(1, dt);

      let newIndex = this.currentSlide;

      // Avanza o retrocede si crossó umbral O velocidad alta
      if (
        Math.abs(dx) > slideWidth * SWIPE_THRESHOLD_RATIO ||
        velocity > VELOCITY_THRESHOLD
      ) {
        newIndex = this.currentSlide + (dx < 0 ? 1 : -1);
      }

      // Clamp en los bordes — bounce-back style (sin wrap loop por ahora;
      // wrap real requiere clonar slides para evitar ghost space en la
      // animación).
      const len = this.slides.length;
      newIndex = Math.max(0, Math.min(len - 1, newIndex));

      this.goToSlide(newIndex, { animate: true });
    };

    this.mainImageWrap.addEventListener('touchend', endHandler, { passive: true });
    this.mainImageWrap.addEventListener('touchcancel', endHandler, { passive: true });
  }

  // ─────────────────────────────────────────────────────────────────────
  //  Mobile bottom sheets (variant / volume / priceInfo)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Bind click handlers for all sheet open triggers + close buttons +
   * backdrop. Triggers viven en cualquier parte del DOM con
   * [data-pp-sheet-open="<name>"]. Close cierra el sheet activo.
   * ESC también cierra. Body scroll lock activado mientras abierto.
   */
  bindSheetEvents() {
    this.activeSheet = null;

    // OPEN triggers — event delegation en root.
    this.root.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-pp-sheet-open]');
      if (opener && this.root.contains(opener)) {
        e.preventDefault();
        const name = opener.dataset.ppSheetOpen;
        if (name) this.openSheet(name);
      }
    });

    // CLOSE handler — cierra si:
    //   (a) click en [data-pp-sheet-close] (close btn, backdrop si capta)
    //   (b) click en el SHEET ELEMENT mismo (afuera del panel)
    //
    // El (b) es crítico: el inspector reveló que cuando el user tap
    // arriba del panel (área "backdrop"), el e.target es el sheet
    // element directamente, NO el .pp-pdp-sheet__backdrop child. El
    // backdrop parece no capturar el evento — posible interferencia
    // de algún CSS reset que le aplica pointer-events: none. Así que
    // detectamos el caso "target === sheet" y cerramos.
    //
    // Listener en DOCUMENT con capture: true — corre antes que cualquier
    // bubble-phase stopPropagation downstream.
    const closeHandler = (e) => {
      if (!this.activeSheet) return;
      const sheet = this.activeSheet;
      const panel = sheet.querySelector('[data-pp-sheet-panel]');

      // Caso (b): click directamente en el sheet element (área backdrop).
      // El target es exactamente el sheet, no un descendant.
      if (e.target === sheet) {
        e.preventDefault();
        e.stopPropagation();
        this.closeSheet();
        return;
      }

      // Caso (a): click en un [data-pp-sheet-close] dentro del sheet
      // (close button o, si el backdrop responde a clicks, backdrop).
      const closer = e.target.closest && e.target.closest('[data-pp-sheet-close]');
      if (closer && sheet.contains(closer)) {
        e.preventDefault();
        e.stopPropagation();
        this.closeSheet();
        return;
      }

      // Si target es descendant del sheet pero NO del panel y NO un
      // closer → es probablemente el backdrop. Cerrar también.
      if (sheet.contains(e.target) && panel && !panel.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        this.closeSheet();
      }
    };
    document.addEventListener('click', closeHandler, true);

    // ESC desktop only — mobile no tiene keyboard físico nativo.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeSheet) {
        this.closeSheet();
      }
    });
  }

  openSheet(name) {
    // Look up: si el sheet ya fue movido al body en una apertura previa
    // ya no vive en this.root → buscamos primero en root, luego en body.
    const sheet =
      this.root.querySelector(`[data-pp-sheet="${CSS.escape(name)}"]`) ||
      document.body.querySelector(`:scope > [data-pp-sheet="${CSS.escape(name)}"]`);
    if (!sheet) return;

    if (this.activeSheet && this.activeSheet !== sheet) {
      this.closeSheet();
    }

    // Mover al body para escapar cualquier stacking context o
    // pointer-events restriction de ancestors. El sheet es fixed-position
    // así que cambiar de parent NO afecta su renderizado, pero SÍ lo
    // libera de containing block + paint order del section.
    if (sheet.parentElement !== document.body) {
      document.body.appendChild(sheet);
    }

    sheet.removeAttribute('hidden');
    sheet.setAttribute('aria-hidden', 'false');
    // Force reflow so transitions kick in
    void sheet.offsetWidth;
    sheet.classList.add('is-open');
    document.body.classList.add('pp-pdp-sheet-open');
    this.activeSheet = sheet;

    // Bind direct close listener on backdrop + close btn (idempotente).
    // Esto es backup además del document-capture handler — si por algún
    // motivo el capture handler no corre, este sí, porque está bindeado
    // ON THE ELEMENT directamente.
    sheet.querySelectorAll('[data-pp-sheet-close]').forEach((el) => {
      if (el._ppCloseBound) return;
      el._ppCloseBound = true;
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeSheet();
      });
    });
  }

  closeSheet() {
    if (!this.activeSheet) return;
    const sheet = this.activeSheet;
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      sheet.setAttribute('hidden', '');
    }, 260);
    document.body.classList.remove('pp-pdp-sheet-open');
    this.activeSheet = null;
  }

  /**
   * Mobile-only product tabs pills (Ficha Técnica / Descripción / Cómo
   * Utilizar / Preguntas Frecuentes). Click en una pill activa el panel
   * correspondiente y oculta los demás. Soporta múltiples instancias
   * (cada [data-pp-product-tabs] es independiente).
   */
  /**
   * Reviews "Ver Más" button — one-shot expand. Remueve is-collapsed
   * de todas las reseñas ocultas (más allá del 5to índice) y elimina
   * el botón. No hay "ver menos" — una vez expandidas, quedan así.
   */
  bindReviewsExpand() {
    const expandBtn = this.root.querySelector('[data-pp-reviews-expand]');
    if (!expandBtn) return;
    const list = this.root.querySelector('[data-pp-reviews-list]');
    if (!list) return;

    expandBtn.addEventListener('click', () => {
      const collapsed = list.querySelectorAll('.pp-pdp-reviews__item.is-collapsed');
      collapsed.forEach((item) => item.classList.remove('is-collapsed'));
      expandBtn.setAttribute('aria-expanded', 'true');
      expandBtn.remove();
    });
  }

  bindProductTabsEvents() {
    const tabContainers = this.root.querySelectorAll('[data-pp-product-tabs]');
    tabContainers.forEach((container) => {
      const pills = container.querySelectorAll('[data-pp-tab-target]');
      const panels = container.querySelectorAll('[data-pp-tab-panel]');

      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          const target = pill.dataset.ppTabTarget;
          if (!target) return;

          pills.forEach((p) => {
            const isActive = p.dataset.ppTabTarget === target;
            p.classList.toggle('is-active', isActive);
            p.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });

          panels.forEach((panel) => {
            const isActive = panel.dataset.ppTabPanel === target;
            panel.classList.toggle('is-active', isActive);
            if (isActive) {
              panel.removeAttribute('hidden');
            } else {
              panel.setAttribute('hidden', '');
            }
          });
        });
      });
    });
  }

  handleOptionChange(input) {
    const groupName = input.name;
    const group = this.root.querySelectorAll(`input[name="${CSS.escape(groupName)}"]`);
    group.forEach((el) => {
      const label = el.closest('.pp-main-product__option-value');
      if (!label) return;
      if (el.checked) {
        label.classList.add('is-checked');
      } else {
        label.classList.remove('is-checked');
      }
    });

    const legend = input.closest('.pp-main-product__option')?.querySelector('[data-pp-selected-value]');
    if (legend) legend.textContent = input.value;

    this.syncVariant();
  }

  syncVariant() {
    if (!this.productJson) return;

    const selectedValues = Array.from(this.optionInputs)
      .filter((el) => el.checked)
      .sort((a, b) => (a.dataset.ppOptionPosition || 0) - (b.dataset.ppOptionPosition || 0))
      .map((el) => el.value);

    const match = this.productJson.variants.find((v) =>
      v.options.every((opt, idx) => opt === selectedValues[idx])
    );
    if (!match) return;

    if (this.variantIdInput) this.variantIdInput.value = String(match.id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    window.history.replaceState({}, '', url);

    this.basePrice = match.price;
    this.comparePrice = match.compare_at_price || 0;
    // Variant cambió → recomputar cartQty para el nuevo variant (puede
    // tener distinto qty en el carrito o no estar). applyCartQty llama
    // recalcPrice internamente.
    this.applyCartQty();
  }

  currentQty() {
    if (!this.qtyInput) return 1;
    const v = parseInt(this.qtyInput.value, 10);
    return isNaN(v) || v < 1 ? 1 : v;
  }

  /**
   * ¿La variante actual está pactada para este comprador? Y si lo está,
   * ¿qué precio unitario (centavos) le toca a esta cantidad según su
   * escalera de mayoreo? null = sin pacto (aplica la conducta genérica).
   * LA LEY (espejo de la Function de volumen): en variante pactada el %
   * de volumen NUNCA aplica; el precio es el pacto plano o su escalón.
   */
  pactoUnitCentsFor(qty) {
    const vid = this.variantIdInput ? String(this.variantIdInput.value) : '';
    if (!vid || this.pactadoIds.indexOf(vid) === -1) return null;
    let unit = this.basePrice; // pacto plano (contextual)
    const breaks = this.pactoBreaks[vid];
    if (Array.isArray(breaks)) {
      for (const b of breaks) {
        if (Array.isArray(b) && qty >= b[0] && b[1] > 0 && b[1] < unit) {
          unit = b[1];
        }
      }
    }
    return unit;
  }

  /**
   * Los renglones de volumen (snippets/pp-volume-discount.liquid) dicen su %
   * contra el TACHADO y su precio por pieza con el precio de ESTA variante.
   * Al cambiar de variante (o en variante pactada, donde el tachado no
   * aplica) se los manda repintar; las cuentas viven en
   * assets/pp-volume-discount.js (porcentajeTotalDeEscalon / precioPorPieza),
   * un solo dueño para el SSR y el repintado.
   */
  sincronizarEtiquetasDeVolumen(sinTachado) {
    const widget = this.root.querySelector('[data-pp-volume-discount]');
    if (!widget) return;
    widget.dispatchEvent(new CustomEvent('pp:volumen-precio', {
      detail: { base: this.basePrice, compare: this.comparePrice, sinTachado: !!sinTachado }
    }));
  }

  /**
   * QUE NADA SE MUEVA AL CAMBIAR LA CANTIDAD (26-oct-2026, Dirección: «de 45 a
   * 46 empuja todo»). Las cifras ya van tabulares (CSS), así que el mismo
   * número de dígitos mide lo mismo. Lo que queda es el precio grande que
   * cambia de largo ($205 → $1,642.05): medido, la columna «4,500 bolsas /
   * $1.64 MXN / bolsa» brincaba de x=749 a x=843. La leyenda «MXN · IVA
   * incluido» se queda el hueco (ancho mínimo que sólo crece), y la columna
   * no regresa al bajar la cantidad. Al cambiar de variante (otro precio
   * base) vuelve a empezar.
   */
  fijarAnchoDelPrecio() {
    const precio = this.priceCurrentEl;
    const leyenda = this.priceCaptionEl;
    if (!precio || !leyenda) return;
    if (this.anchoDelPrecioBase !== this.basePrice) {
      this.anchoDelPrecioBase = this.basePrice;
      this.anchoDelPrecioMax = 0;
    }
    leyenda.style.minWidth = '';
    const anchoLeyenda = leyenda.getBoundingClientRect().width;
    const total = precio.getBoundingClientRect().width + anchoLeyenda;
    if (!total) return;
    if (total > this.anchoDelPrecioMax) this.anchoDelPrecioMax = total;
    const hueco = this.anchoDelPrecioMax - total;
    if (hueco > 0.5) leyenda.style.minWidth = (anchoLeyenda + hueco).toFixed(1) + 'px';
  }

  recalcPrice() {
    if (!this.priceBlock || !this.basePrice) return;

    const qty = this.currentQty();
    const subtotalCents = this.basePrice * qty;
    const pactoUnit = this.pactoUnitCentsFor(qty);
    const off = pactoUnit !== null ? 0 : this.activeTierOff;

    this.sincronizarEscaleraPactada(qty);

    let displayCents = subtotalCents;
    let originalCents = 0;
    let savingsCents = 0;

    if (pactoUnit !== null) {
      // Variante pactada: el precio mostrado es EXACTAMENTE el que el
      // checkout cobra — escalón × qty. Tachado/ahorro contra el pacto
      // plano solo cuando el escalón ya bajó el precio.
      displayCents = pactoUnit * qty;
      if (pactoUnit < this.basePrice) {
        originalCents = subtotalCents;
        savingsCents = subtotalCents - displayCents;
      }
    } else if (off > 0) {
      displayCents = Math.round(subtotalCents * (100 - off) / 100);
    }

    // Compare_at_price gana jerarquía sobre volume tier: si la variante
    // tiene comparePrice > basePrice, el tachado SIEMPRE es comparePrice
    // × qty (no el subtotal de basePrice). Así, cuando el cliente sube
    // qty al primer tier, el ahorro suma el discount de sale + el de
    // volumen, en vez de "perder" la referencia del compare_at.
    // (En variante pactada NO aplica: el pacto es el precio del cliente
    // y un compare_at de lista inflaría el "ahorro" con humo.)
    if (pactoUnit === null && this.comparePrice && this.comparePrice > this.basePrice) {
      originalCents = this.comparePrice * qty;
      savingsCents = originalCents - displayCents;
    } else if (pactoUnit === null && off > 0) {
      originalCents = subtotalCents;
      savingsCents = subtotalCents - displayCents;
    }

    if (this.priceCurrentEl) {
      this.priceCurrentEl.textContent = this.formatMoneyBigPrice(displayCents);
      // El cotizador de envío (assets/pp-envio-cp.js) suma el envío a ESTE
      // total y recotiza al cambiar la cantidad o la variante.
      this.priceCurrentEl.setAttribute('data-pp-total-cents', String(displayCents));
      document.dispatchEvent(new CustomEvent('pp:precio-total', { detail: { cents: displayCents, qty: qty } }));
    }
    if (this.priceOriginalEl) {
      this.priceOriginalEl.textContent = originalCents > 0 ? this.formatMoney(originalCents) : '';
    }
    if (this.priceSavingsEl) {
      if (savingsCents > 0) {
        this.priceSavingsEl.textContent = 'Ahorras ' + this.formatMoney(savingsCents) + ' MXN';
        this.priceSavingsEl.hidden = false;
      } else {
        this.priceSavingsEl.hidden = true;
      }
    }
    // Etiqueta roja "-X%" a la izquierda del tachado: el mismo ahorro de
    // "Ahorras $X" expresado como porcentaje del tachado. Con 1 paquete
    // coincide con la etiqueta de la tarjeta de colección; al subir de
    // escalón suma el volumen (oferta 13% + volumen 18% = 29%).
    // Solo con precio tachado real (oferta): un producto sin compare_at
    // conserva su ficha de siempre aunque cruce un escalón de volumen.
    {
      const enOferta = pactoUnit === null && this.comparePrice > this.basePrice;
      const pct = enOferta && originalCents > 0 ? Math.round(savingsCents * 100 / originalCents) : 0;
      this.priceBadgeEls.forEach((el) => {
        el.textContent = pct > 0 ? '-' + pct + '%' : '';
        el.hidden = pct <= 0;
      });
      // Reacomodo de celular solo con oferta (cambio de variante incluido).
      if (this.priceRowEl) {
        this.priceRowEl.classList.toggle('pp-main-product__price-row--oferta', pct > 0);
      }
    }
    this.sincronizarEtiquetasDeVolumen(pactoUnit !== null);

    // El cluster derecho (tachado + savings) se muestra/oculta
    // como unidad. Solo aparece cuando hay descuento activo.
    if (this.priceRightEl) {
      this.priceRightEl.hidden = originalCents === 0 && savingsCents === 0;
    }

    if (this.chipUnitValueEl && this.unitCount > 0) {
      // "$5.14 MXN / caja" — per-piece price with currency + singular unit
      const perUnitCents = Math.round((displayCents / qty) / this.unitCount);
      const singular = (this.unitSingular || 'pieza').toLowerCase();
      this.chipUnitValueEl.textContent = this.formatMoney(perUnitCents) + ' MXN / ' + singular;
    }

    if (this.chipTotalValueEl && this.unitCount > 0 && this.unitWord) {
      const totalPieces = this.unitCount * qty;
      const word = this.pluralizeUnit(this.unitWord, this.unitSingular, totalPieces);
      this.chipTotalValueEl.textContent = totalPieces.toLocaleString('es-MX') + ' ' + word;
    }

    this.fijarAnchoDelPrecio();

    // Sticky bar mirror — precio + count/each replicados del main
    // block. La unit column sale solo cuando hay unidades parseadas
    // (el liquid la renderiza condicionalmente).
    if (this.stickyCurrentEl) {
      this.stickyCurrentEl.textContent = this.formatMoneyBigPrice(displayCents);
    }
    if (this.stickyChipUnitValueEl && this.unitCount > 0) {
      const perUnitCents = Math.round((displayCents / qty) / this.unitCount);
      const singular = (this.unitSingular || 'pieza').toLowerCase();
      this.stickyChipUnitValueEl.textContent = this.formatMoney(perUnitCents) + ' MXN / ' + singular;
    }
    if (this.stickyChipTotalValueEl && this.unitCount > 0 && this.unitWord) {
      const totalPieces = this.unitCount * qty;
      const word = this.pluralizeUnit(this.unitWord, this.unitSingular, totalPieces);
      this.stickyChipTotalValueEl.textContent = totalPieces.toLocaleString('es-MX') + ' ' + word;
    }
    if (this.stickyQtyInput && parseInt(this.stickyQtyInput.value, 10) !== qty) {
      this.stickyQtyInput.value = String(qty);
    }

    // Mobile sticky price block — 3 spans reactivos:
    //   pack (e.g. "225 cajas")  = unitCount × qty (con plural correcto)
    //   price (e.g. "$2,205")     = displayCents (qty × base con tier off)
    //   per-unit (e.g. "$9.80/caja") = displayCents / qty / unitCount
    // Per-unit con decimales conditional: formatMoney ya stripsea `.00`.
    if (this.stickyMobilePackEl && this.unitCount > 0 && this.unitWord) {
      const totalPieces = this.unitCount * qty;
      const word = this.pluralizeUnit(this.unitWord, this.unitSingular, totalPieces);
      this.stickyMobilePackEl.textContent = totalPieces.toLocaleString('es-MX') + ' ' + word.toLowerCase();
    }
    if (this.stickyMobileCurrentEl) {
      this.stickyMobileCurrentEl.textContent = this.formatMoneyBigPrice(displayCents).replace(/\s*MXN\s*$/, '');
    }
    if (this.stickyMobilePerUnitEl && this.unitCount > 0) {
      const perUnitCents = Math.round((displayCents / qty) / this.unitCount);
      const singular = this.stickyMobilePerUnitEl.dataset.ppStickyMobileSingular
        || (this.unitSingular || this.unitWord || 'pieza').toLowerCase();
      this.stickyMobilePerUnitEl.textContent = this.formatMoney(perUnitCents).replace(/\s*MXN\s*$/, '') + '/' + singular;
    }

    // Cart state labels — refleja delta entre qty input y cart actual.
    this.updateCtaLabels();
  }

  /**
   * For products sold "por unidad" (single pieces), pluralize the
   * singular word when qty > 1. For products sold "por paquete",
   * the metafield word is already the plural form of the contents
   * (e.g. "Cajas" — one pack contains 25 Cajas), so we always use
   * the plural as-is.
   */
  pluralizeUnit(plural, singular, count) {
    if (this.sellBy === 'unidad') {
      if (count === 1) {
        return singular || plural;
      }
      // Spanish rule of thumb: words ending in a vowel → +s, consonant → +es
      const base = singular || plural;
      const last = base.slice(-1).toLowerCase();
      if ('aeiou'.indexOf(last) >= 0) return base + 's';
      return base + 'es';
    }
    return plural;
  }

  /**
   * Format an amount in cents as MXN currency. Trailing `.00` is
   * dropped when the amount has no fractional cents (psychological
   * pricing: "$756" feels smaller than "$756.00"). Non-zero cents
   * always show two decimals, e.g. "$642.60".
   */
  formatMoney(cents) {
    const hasCents = cents % 100 !== 0;
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  /**
   * El precio grande, IGUAL en celular y en escritorio (Dirección, 24-oct-2026,
   * regla de §27): con centavos se ponen, sin centavos se quitan — «$748.24»,
   * «$299». Antes en celular se redondeaba a pesos y el mismo total decía
   * $748 en el teléfono y $748.24 en la computadora.
   */
  formatMoneyBigPrice(cents) {
    return this.formatMoney(cents);
  }

  /**
   * El mínimo y el escalón del control, leídos del HTML.
   *
   * Viven en `min` y `step` del input, que los pinta Liquid desde
   * `custom.moq` y `custom.multiplo`. NO se vuelven a derivar aquí: son del
   * proveedor, y una segunda copia en JS es la próxima divergencia. Sin
   * metacampos el HTML trae 1 y 1, que es como se portaba antes.
   */
  qtyReglas() {
    const min = parseInt(this.qtyInput.getAttribute('min'), 10);
    const paso = parseInt(this.qtyInput.getAttribute('step'), 10);
    return {
      min: Number.isFinite(min) && min > 0 ? min : 1,
      paso: Number.isFinite(paso) && paso > 0 ? paso : 1,
    };
  }

  /**
   * El número pedible más cercano HACIA ARRIBA.
   *
   * Siempre arriba, nunca abajo: bajar le entrega al cliente menos de lo que
   * pidió sin decirle. Y los escalones cuelgan del MÍNIMO, no del cero —
   * 120 de 24 en 24 da 120, 144, 168…, no 120, 128, 152.
   */
  qtyValida(n) {
    const { min, paso } = this.qtyReglas();
    if (!Number.isFinite(n) || n <= min) return min;
    return min + Math.ceil((n - min) / paso) * paso;
  }

  adjustQty(delta) {
    if (!this.qtyInput) return;
    const { min, paso } = this.qtyReglas();
    const current = parseInt(this.qtyInput.value, 10) || min;
    // El paso del botón ES el escalón del proveedor, no 1.
    const next = Math.max(min, current + delta * paso);
    this.qtyInput.value = String(this.qtyValida(next));
    this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Acomoda lo TECLEADO al escalón. Va en `change` (al salir del campo) y no
   * en `input`: corregir mientras alguien escribe le mueve el cursor y le
   * pelea el número a media tecla.
   */
  acomodarQtyTecleada() {
    if (!this.qtyInput) return;
    const crudo = parseInt(this.qtyInput.value, 10);
    const bueno = this.qtyValida(crudo);
    if (String(bueno) === this.qtyInput.value) return;
    this.qtyInput.value = String(bueno);
    this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Fetch /cart.js y guardar el cart cacheado. Después aplica el qty
   * del variant actual al stepper + label CTA.
   */
  async loadCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      this.cart = await res.json();
    } catch (e) {
      this.cart = null;
    }
    this.applyCartQty();
  }

  /**
   * Handler de evento pp:cart-updated. Si el detail trae cart entero,
   * usarlo; si no, refetch.
   */
  handleCartUpdated(e) {
    if (e && e.detail && Array.isArray(e.detail.items)) {
      this.cart = e.detail;
      this.applyCartQty();
    } else {
      this.loadCart();
    }
  }

  /**
   * Calcula cartQty del variant actual desde this.cart (todas sus
   * líneas). El contador NO se toca: es lo que el cliente va a agregar
   * (TB-1); lo del carrito lo dice la línea «Ya tienes N…».
   */
  applyCartQty() {
    const vid = this.variantIdInput ? parseInt(this.variantIdInput.value, 10) : NaN;
    let qty = 0;
    if (vid && this.cart && Array.isArray(this.cart.items)) {
      for (const item of this.cart.items) {
        if (item.variant_id === vid) qty += item.quantity;
      }
    }
    this.cartQty = qty;
    this.recalcPrice();
  }

  /**
   * TB-1: los botones no cambian con el carrito (siempre su etiqueta del
   * Liquid: Agregar / Pre-ordenar / Agotado). Desde el 26-oct-2026 tampoco
   * hay línea «Ya tienes N…»: no se pinta nada según el carrito.
   */
  updateCtaLabels() {}

  /** Abre el carrito lateral con el producto y sus relacionados curados
   * (sin ellos el panel decía «No tenemos recomendaciones»). */
  abrirCarrito(addedItem) {
    const sectionRoot = document.querySelector('.pp-main-product');
    const productId = addedItem
      ? addedItem.product_id
      : sectionRoot ? parseInt(sectionRoot.dataset.ppProductId, 10) : null;
    let relatedProducts = null;
    const relEl = document.querySelector('[data-pp-related-products]');
    if (relEl && relEl.textContent) {
      try {
        relatedProducts = JSON.parse(relEl.textContent);
      } catch (e) {
        relatedProducts = null;
      }
    }
    const detail = { productId: productId, relatedProducts: relatedProducts };
    if (addedItem) detail.addedItem = addedItem;
    document.dispatchEvent(new CustomEvent('pp:cart-drawer:open', { detail: detail }));
  }

  /**
   * Intercept the PDP add-to-cart submit. AJAX-add and open the PP cart
   * drawer instead of navigating to /cart. Falls back to native submit
   * on error so the user always reaches the cart eventually.
   */
  async handleAddToCart(e, form) {
    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"], button[name="add"]');
    const originalLabel = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Agregando…';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw err;
      }
      const item = await res.json();
      this.abrirCarrito(item);
      document.dispatchEvent(new CustomEvent('pp:cart-updated'));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[pp-main-product] AJAX cart op failed', err);
      // Fallback a native submit (cart/add nativo).
      form.submit();
      return;
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    }
  }
}

function initAll() {
  document.querySelectorAll('.pp-main-product').forEach((root) => {
    if (root.dataset.ppInit === 'true') return;
    root.dataset.ppInit = 'true';
    new PpMainProduct(root);
  });
  initReviewScroll();
}

// Scroll suave al hacer clic en el rating del PDP -> seccion de resenas
// (#judgeme_product_reviews). Delegation a nivel document (una sola vez), asi sirve en
// mobile y desktop y sobrevive re-renders de la seccion. scrollIntoView
// respeta el scroll-margin-top de .pp-reviews-mount y prefers-reduced-motion.
function initReviewScroll() {
  if (document.body.dataset.ppReviewScrollBound) return;
  document.body.dataset.ppReviewScrollBound = '1';
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-pp-review-scroll]');
    if (!trigger) return;
    // Mobile: #pp-pdp-reviews (resumen, mobile-only). Desktop: ese resumen esta
    // oculto -> caemos a la seccion de resenas #pp-reviews-section. Ninguno es
    // #judgeme_product_reviews, asi que Loox no intercepta el clic (sin ga('send') error).
    let target = document.querySelector('#pp-pdp-reviews');
    if (!target || target.offsetParent === null) {
      target = document.querySelector('#pp-reviews-section');
    }
    if (!target || target.offsetParent === null) return; // nada visible: no bloquear
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

if (document.readyState !== 'loading') {
  initAll();
} else {
  document.addEventListener('DOMContentLoaded', initAll);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.pp-main-product')) initAll();
});
